import type { OrderStatus } from '@blackarrow/types';

/**
 * A tile that is not a status: refusals waiting on the shelf to go back to
 * their merchant. `returned_to_hub` is shared by that branch and the cash one,
 * so this is a split of the hub fold, derived server-side from the event that
 * put the parcel there (Support\DeclinedReturn) — there is no such status.
 */
export type DashboardStage = OrderStatus | 'declined_return_to_hub';

/** Names for the tiles that are not statuses. */
export const DASHBOARD_STAGE_LABELS: Partial<Record<DashboardStage, string>> = {
    declined_return_to_hub: 'Declined return to hub',
};

/**
 * The dashboard's three rows, each a fixed list: the same tiles in the same
 * places whether the day was busy or empty. `StageGrid` renders what it is
 * given, zeros included, so these lists are the whole layout. See
 * docs/decisions/0044-dashboard-counts-events-on-the-day.md, which replaces
 * the nine-tile pipeline of ADR 0017.
 *
 * Each tile is what happened on the chosen day, not where parcels are now.
 * `returned_to_hub` is folded into `arrived_at_hub` by the server, and the
 * refusals coming back are split out as `declined_return_to_hub` (ADR 0039).
 * `cash_reconciled`, `remitted`, `on_hold` and `declined_by_merchant` have no
 * tile here; Reports and the orders table still have them.
 */
export const PICKUP_STAGES: readonly DashboardStage[] = ['new', 'ready_for_pickup'];

export const OPERATION_STAGES: readonly DashboardStage[] = [
    'picked_up',
    'arrived_at_hub',
    'out_for_delivery',
    'delivered',
    'rescheduled',
];

/**
 * The second grid: everything that was refused, in the order a refused parcel
 * travels. Declined at the door, back on the shelf, in a rider's bag, and
 * finally with its merchant.
 *
 * Its own section rather than four tiles scattered through the pipeline,
 * because a refused parcel is not on the delivery path any more and ops reads
 * it as a separate question: "what is coming back, and where is it?". Still a
 * fixed list, so it keeps the property the grid depends on (see above).
 */
export const DECLINED_STAGES: readonly DashboardStage[] = [
    'declined',
    'declined_return_to_hub',
    'declined_on_way_to_merchant',
    'declined_returned_to_merchant',
];

/**
 * Tiles that stand for more than one status, in the live view.
 *
 * A tile's value is the sum over its statuses, and clicking it opens the
 * orders table filtered to all of them — so the number on the card and the
 * length of the list behind it always agree.
 */
const STAGE_ROLLUP: Partial<Record<DashboardStage, readonly OrderStatus[]>> = {
    arrived_at_hub: ['arrived_at_hub', 'returned_to_hub'],
    declined_return_to_hub: ['returned_to_hub'],
};

/** How a tile splits `returned_to_hub`, where it does. */
const STAGE_SPLIT: Partial<Record<DashboardStage, DeclinedReturnSplit>> = {
    // The hub tile is everything on the shelf EXCEPT the refusals, which have
    // their own tile — otherwise one parcel is counted on two cards.
    arrived_at_hub: 'exclude',
    // On the shelf, with or without a rider assigned. Once the rider scans one
    // it has a status of its own and a plain tile after this one (ADR 0042).
    declined_return_to_hub: 'only',
};

/** How a tile splits the shelf's `returned_to_hub` parcels. */
export type DeclinedReturnSplit = 'exclude' | 'only';

/** The statuses a live tile counts. Most tiles count exactly themselves. */
export function statusesFor(stage: DashboardStage): readonly OrderStatus[] {
    return STAGE_ROLLUP[stage] ?? [stage as OrderStatus];
}

/** The `declined_return` filter a live tile's list needs, if it splits the shelf. */
export function declinedReturnFor(stage: DashboardStage): DeclinedReturnSplit | undefined {
    return STAGE_SPLIT[stage];
}

/**
 * A live tile's value, summed across whatever it stands for: where parcels are
 * now, today's bookings plus everything still in flight.
 *
 * `declinedReturn` is the server's count of refusals on the shelf. A payload
 * that predates it reads as none.
 */
export function liveStageTotal(
    stage: DashboardStage,
    byStatus: Partial<Record<OrderStatus, number>>,
    declinedReturn = 0,
): number {
    if (stage === 'declined_return_to_hub') {
        return declinedReturn;
    }

    const total = statusesFor(stage).reduce((sum, status) => sum + (byStatus[status] ?? 0), 0);

    // Read-model counts can lag the live count by a projector run; never show
    // a negative.
    return STAGE_SPLIT[stage] === 'exclude' ? Math.max(0, total - declinedReturn) : total;
}

/**
 * The tiles a past day can speak to: things that happen once, on a day, and
 * stay true. The rest (booked, ready, at the hub, out now, on the shelf, on the
 * way back) describe where a parcel is at a moment, so a past day has nothing
 * honest to say about them and they read 0.
 */
export const HISTORICAL_STAGES: readonly DashboardStage[] = [
    'picked_up',
    'delivered',
    'rescheduled',
    'declined',
    'declined_returned_to_merchant',
];

/**
 * Tiles that read today's events even in the live view.
 *
 * `delivered` is a waypoint, not a resting place: a parcel moves on to
 * `cash_reconciled` once its cash is counted in, often the same evening. By
 * status the tile would shrink as the cash desk works, and read 0 by night on
 * a day that delivered fifty. By event it is "delivered today" and stays so.
 */
export const LIVE_DAY_STAGES: readonly DashboardStage[] = ['delivered'];

/** A past day's tile value, from the event counts. 0 for tiles it cannot speak to. */
export function dayStageTotal(
    stage: DashboardStage,
    byStatus: Partial<Record<OrderStatus, number>>,
): number {
    return HISTORICAL_STAGES.includes(stage) ? (byStatus[stage as OrderStatus] ?? 0) : 0;
}

/** Every tile, for validating a link that names one. */
export const DASHBOARD_TILES: readonly DashboardStage[] = [
    ...PICKUP_STAGES,
    ...OPERATION_STAGES,
    ...DECLINED_STAGES,
];
